import { ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useForm, UseFormMethods, Control } from 'react-hook-form';

import { GqlGetCollectionsQuery } from 'types/graphql';
import { useGetCollections } from '../../hooks/use-get-collections';
import { AddCollectionModalProps } from './add-collection-modal.types';
import { getMenuSectionProductFilter, getProductIds } from '../../homepage-form.utils';
import { Products } from '../../homepage-form.types';

type CollectionFromQuery = GqlGetCollectionsQuery['getCollections'][0];

type CollectionFormData = {
  collectionId: string;
  sectionName: string;
  sorting: string;
};

type UseAddCollectionModalReturn = {
  control: Control<CollectionFormData>;
  handleSubmit: UseFormMethods<CollectionFormData>['handleSubmit'];
  collectionId: string;
  onSubmit: (data: CollectionFormData) => void;
  handleChangeCollection: (e: ChangeEvent<HTMLSelectElement>) => void;
  collectionsToDisplay: CollectionFromQuery[];
  loading: boolean;
  isEditingSection: boolean;
};

export const useAddCollectionModal = ({
  handleAddSection,
  menuSection,
  menuSections,
  sectionTypeKey,
  onToggle,
  products,
}: AddCollectionModalProps): UseAddCollectionModalReturn => {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const { collections, loading } = useGetCollections();
  const isEditingSection = !!menuSection;

  const existingCollectionIds = menuSections
    ?.filter((section) => section.sectionType === sectionTypeKey)
    ?.map((section) => section.collectionId);

  const collectionsToDisplay = isEditingSection
    ? collections
    : collections.filter((collection) => !existingCollectionIds?.includes(collection.id));

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { control, handleSubmit, watch, setValue } = useForm<CollectionFormData>({
    defaultValues: {
      collectionId: menuSection?.collectionId ?? '',
      sectionName: menuSection?.sectionName ?? '',
      sorting: menuSection?.sortBy ?? 'popular',
    },
  });

  const collectionId = watch('collectionId');

  const onSubmit = (data: CollectionFormData): void => {
    const filterMethod = getMenuSectionProductFilter(sectionTypeKey);
    const foundProducts = filterMethod({
      items: { products, offers: null },
      menuSection: { collectionId: data.collectionId },
    });

    const productIds = getProductIds(foundProducts as Products);

    const count = productIds.length;

    if (isEditingSection) {
      handleAddSection({
        ...menuSection,
        sectionName: data.sectionName,
        label: data.sectionName,
        sortBy: data.sorting,
        count,
      });
    } else {
      handleAddSection({
        id: uuid(),
        dispensaryId,
        sectionType: sectionTypeKey,
        position: 0,
        count,
        collectionId: data.collectionId,
        sectionName: data.sectionName,
        label: data.sectionName,
        sortBy: data.sorting,
      });
    }

    onToggle();
  };

  const handleChangeCollection = (e: ChangeEvent<HTMLSelectElement>): void => {
    const selectedCollectionId = e.target.value;
    setValue('collectionId', selectedCollectionId);

    const selectedCollection = collections.find((collection) => collection.id === selectedCollectionId);
    if (selectedCollection && !watch('sectionName')) {
      setValue('sectionName', selectedCollection.title);
    }
  };

  return {
    control,
    handleSubmit,
    collectionId,
    onSubmit,
    handleChangeCollection,
    collectionsToDisplay,
    loading,
    isEditingSection,
  };
};
